// Centralized site data for Ohana Property Services

export const BRAND = {
  name: 'Ohana Property Services',
  legal: 'Ohana Property & Transition Services LLC',
  email: 'ohanas@ohanapropertyexperts.com',
  phone: '(812) 302-6833',
  phoneTel: '+18123026833',
  phoneSms: '+18123026833',
  serviceArea: 'Central Indiana',
  city: 'Columbus',
  state: 'IN',
  postalCode: '47201',
  county: 'Bartholomew County',
  origin: 'https://ohanapropertyexperts.com',
  disclaimer:
    'Ohana Property Services is a trade name of Ohana Property & Transition Services LLC, a registered Indiana limited liability company. All rights reserved.',
};

export const FOUNDER = {
  name: 'Olga Ortega',
  jobTitle: 'Founder & Owner',
  alumniOf: 'Purdue University',
  priorCareer: 'Social Worker (10+ years)',
  identity: 'Woman-Owned · Latina-Owned · Two-Time Cancer Survivor',
  photo: '/ohana/olga-cancer-survivor.jpg',
  photoAlt:
    'Olga Ortega, founder of Ohana Property & Transition Services and two-time cancer survivor, photographed for breast cancer awareness',
};

// External profile URLs used for schema `sameAs` and footer links.
// Operator-supplied. Populated as URLs are confirmed.
export const SAMEAS_URLS = [
  'https://www.linkedin.com/company/ohana-property-transition-services-llc/',
  'https://www.facebook.com/groups/587428724736971/',
];

// Founder-only `sameAs`. Olga's personal profiles, kept separate from the
// company URLs so the Person schema is unambiguous.
export const FOUNDER_SAMEAS_URLS = [
  'https://www.linkedin.com/in/olga-ortega-lopez-42b792166',
];

export const SERVICE_AREAS = [
  'Columbus',
  'Seymour',
  'Edinburgh',
  'Franklin',
  'Greenwood',
  'Shelbyville',
  'North Vernon',
  'Greensburg',
  'Bloomington',
  'Indianapolis Southside',
];

export const TRUST_SIGNALS = [
  { label: 'Licensed & Insured', icon: 'ShieldCheck' },
  { label: '24-48 Hour Turnaround', icon: 'Clock' },
  { label: 'No Hidden Fees', icon: 'Camera' },
  { label: 'Donation-Conscious Recovery', icon: 'HeartHandshake' },
  { label: 'Serving Central Indiana', icon: 'MapPin' },
];

export const SERVICES = [
  {
    slug: 'junk-removal',
    title: 'Junk Removal',
    short: 'One-call removal of furniture, appliances, yard waste, and household clutter.',
    icon: 'Trash2',
    feature: true,
    hero: 'Same-Week Junk Removal Across Central Indiana',
    description:
      'From a single bulky couch to a garage-full of accumulated stuff, our crew shows up fast, loads it out, and hauls it away.',
    forWho:
      'Homeowners ready to reclaim a room. Landlords prepping for new tenants. Anyone who is tired of looking at it.',
    pricingNote: 'Most jobs $100 to $500 depending on volume.',
    items: [
      'Furniture & mattresses',
      'Appliances (working or not)',
      'Yard waste & debris',
      'Garage and basement clutter',
      'Old electronics',
    ],
  },
  {
    slug: 'property-cleanouts',
    title: 'Full Property Cleanouts',
    short: 'Complete top-to-bottom cleanouts for transitions, turnovers, and flips.',
    icon: 'Home',
    feature: true,
    hero: 'Full Property Cleanouts: Move-In Ready in 24 to 48 Hours',
    description:
      'Whether the property is half-full or wall-to-wall, we clear it out efficiently and leave it broom-clean and ready for the next chapter.',
    forWho: 'Estate executors, landlords between tenants, real-estate investors prepping a flip.',
    pricingNote: 'Most full property cleanouts run $800 to $3,000+ based on size and volume.',
    items: [
      'Whole-house contents removal',
      'Garage, attic, basement, shed',
      'Sorting and donation routing',
      'Coordinated multi-day projects',
      'Final broom-clean walkthrough',
    ],
    addonNote:
      'Carpet removal available as an add-on for property cleanouts. Ask about pricing when requesting your quote.',
  },
  {
    slug: 'estate-cleanouts',
    title: 'Estate Cleanouts',
    short: 'Compassionate, careful estate cleanouts handled the way family deserves.',
    icon: 'Heart',
    feature: false,
    hero: 'Estate Cleanouts Handled With Care and Treated Like Family',
    description:
      'Estate transitions are heavy. We move at your pace, set aside items you want kept, route usable goods to donation, and remove the rest.',
    forWho: 'Families settling an estate. Executors and probate attorneys. Realtors prepping inherited properties.',
    pricingNote: 'Typically $1,000 to $3,000+ depending on home size.',
    items: [
      'Sort, save, donate, dispose',
      'Photo & document preservation',
      'Coordinated with realtor timelines',
      'Discreet, respectful crew',
      'Donation-conscious recovery',
    ],
    addonNote:
      'Carpet removal available as an add-on for estate cleanouts. Mention it when submitting your photos and address for a quote.',
  },
  {
    slug: 'appliance-removal',
    title: 'Appliance Removal',
    short: 'Old fridges, washers, dryers, and stoves hauled out and gone the same week.',
    icon: 'Refrigerator',
    feature: false,
    hero: 'Appliance Removal: One Truck, One Trip, Gone',
    description:
      'Working or broken, hooked-up or in the garage, we disconnect (where safe), wrap it, walk it out, and haul it away. No driveway scratches, no scuff marks, no hassle.',
    forWho: 'Homeowners replacing appliances. Landlords swapping out units between tenants. Anyone with a dead fridge in the garage.',
    pricingNote: 'Single appliance pickups typically $75 to $150. Multi-appliance discounts available.',
    items: [
      'Refrigerators, freezers, washers, dryers',
      'Stoves, ovens, dishwashers, microwaves',
      'Water heaters and HVAC components',
      'Working items routed to donation when possible',
      'Eco-friendly disposal of refrigerants',
    ],
  },
  {
    slug: 'furniture-removal',
    title: 'Furniture Removal',
    short: 'Couches, mattresses, dressers, and desks out the door without the back pain.',
    icon: 'Sofa',
    feature: false,
    hero: 'Furniture Removal: We Carry, You Relax',
    description:
      'From a single sectional to an entire bedroom set, we handle the lift, the stairs, and the haul. Tight doorways and narrow stairwells are our specialty.',
    forWho: 'Anyone moving, redecorating, downsizing, or just done with that couch.',
    pricingNote: 'Single piece $75 to $150. Whole-room or multi-room jobs $200 to $600.',
    items: [
      'Couches, sectionals, recliners',
      'Mattresses & box springs',
      'Bedroom & dining sets',
      'Office desks & filing cabinets',
      'Donation-routed when in good condition',
    ],
  },
  {
    slug: 'debris-removal',
    title: 'Debris Removal',
    short: 'Construction debris, renovation waste, and post-project messes cleaned up fast.',
    icon: 'HardHat',
    feature: false,
    hero: 'Debris Removal for Job Sites, Renovations, and Storm Cleanup',
    description:
      'If your contractor left behind drywall, lumber, demo waste, or roofing tear-off, or your weekend project went bigger than planned, we haul it.',
    forWho: 'Homeowners post-renovation, contractors needing a clean site, anyone after a storm.',
    pricingNote: 'Most debris loads $200 to $700 depending on weight and volume.',
    items: [
      'Drywall, lumber, plaster',
      'Tile, flooring tear-off',
      'Roofing & siding waste',
      'Storm and tree debris',
      'Cleaned-up, broom-finished site',
    ],
  },
  {
    slug: 'yard-waste-removal',
    title: 'Yard Waste Removal',
    short: 'Branches, brush, leaves, and overgrowth hauled off your property in one trip.',
    icon: 'TreePine',
    feature: false,
    hero: 'Yard Waste Removal: Clear the Yard, Keep the Weekend',
    description:
      'We load up branches, brush piles, bagged leaves, sod, and overgrowth so your yard goes from wild to walkable without you renting a trailer.',
    forWho: 'Homeowners after a big yard day. Landlords prepping curb appeal. Anyone after a storm.',
    pricingNote: 'Typical yard hauls $150 to $500 depending on volume.',
    items: [
      'Branches, brush, and limbs',
      'Bagged leaves and grass clippings',
      'Sod, mulch, and small landscape debris',
      'Storm-damage cleanup',
      'Compost-routed when possible',
    ],
  },
  {
    slug: 'donation-recovery',
    title: 'Donation-Conscious Recovery',
    short: 'Usable items routed to Indiana families and nonprofits instead of the landfill.',
    icon: 'HandHeart',
    feature: false,
    hero: 'Donation-Conscious Recovery and The Hearth Project',
    description:
      'Through The Hearth Project, we identify usable items during every cleanout and route them to local nonprofits, shelters, and families in need before anything is disposed of.',
    forWho: 'Anyone who wants their cleanout to do more than fill a dumpster.',
    pricingNote: 'Built into every cleanout at no extra cost.',
    items: [
      'On-site sorting during cleanout',
      'Partnerships with Indiana nonprofits',
      'Furniture, appliances, household goods',
      'Documentation available on request',
      'Inquiries: ohanas@ohanapropertyexperts.com',
    ],
  },
];

export const HOW_IT_WORKS = [
  {
    n: '01',
    title: 'Send Photos + Address',
    body: 'Text or email us photos of the property and the items. The more we see, the more accurate the quote.',
    note: 'Large jobs are assessed from your photos so we arrive prepared with the right equipment, the right crew, and the right timeline. We assess before we arrive so there are no wasted trips.',
  },
  {
    n: '02',
    title: 'We Send a Quote',
    body: 'Same-day response in most cases. Transparent, photo-based pricing with no surprise fees.',
  },
  {
    n: '03',
    title: 'You Approve',
    body: 'Pick a window that works for you. We confirm the schedule and give you a clear arrival time.',
  },
  {
    n: '04',
    title: 'We Show Up Fast',
    body: 'Crew arrives, loads it out, sweeps up, and hauls it away. You walk back into a clean space.',
  },
];

export const PRICING_TIERS = [
  {
    name: 'Small Pickup',
    price: '$100 to $250',
    note: 'Truck bed load. 5-8 items. Ground-floor preferred. About 1 hour.',
    anchor: 'Most small pickups start at $149.',
    bullets: ['Truck-bed haul', 'Same-week scheduling', 'Donation routing included'],
    highlight: false,
    cta: 'Get My Free Quote',
  },
  {
    name: 'Medium Cleanout',
    price: '$300 to $800',
    note: 'Half trailer load. 1-2 rooms. Ground-floor preferred. About 2.5 hours.',
    bullets: ['1 to 2 crew, half-day or full-day', 'Sorting & donation', 'Broom-clean finish'],
    highlight: true,
    badge: 'Most Common',
    cta: 'Get My Free Quote',
  },
  {
    name: 'Full Property',
    price: 'Custom Quote',
    note: 'Whole-home cleanouts, estates, and flips. Scope assessed from your photos so we quote upfront with no surprises.',
    bullets: ['Multi-crew, multi-day if needed', 'Donation-conscious recovery', 'Coordinated with realtor / closing'],
    highlight: false,
    cta: 'Get a Custom Quote',
    footnote:
      'Large volume jobs are scoped from photos before scheduling so we show up ready to execute efficiently.',
  },
];

export const ACCEPTANCE_POLICY = {
  accepts: {
    title: 'What Ohana Accepts',
    text:
      'Ohana Property Services accepts standard nonhazardous cleanout items including furniture, household junk, move-out debris, estate cleanout items, and donation-eligible household goods with written permission.',
  },
  doesNotAccept: {
    title: 'What Ohana Does Not Accept',
    text:
      'Ohana Property Services does not accept hazardous waste, medical waste, needles or sharps, chemicals, asbestos-containing materials, or regulated waste unless handled by a properly licensed partner.',
  },
};

export const DISPOSAL_RESOURCES = [
  {
    label: 'Bartholomew County Solid Waste',
    detail: 'For hazardous household items',
    href: 'https://www.bcswmd.com',
    linkLabel: 'bcswmd.com',
    phone: '812-376-2614',
  },
  {
    label: 'Columbus Dept. of Public Works',
    detail: 'For Columbus city residents',
    href: 'https://www.columbus.in.gov/public-works/',
    linkLabel: 'columbus.in.gov/public-works',
    phone: '812-376-2509',
  },
];

export const FAQS = [
  {
    q: 'How do I get a quote?',
    a: 'Send us photos of the property and the address to ohanas@ohanapropertyexperts.com or use the quote form on this page. We typically respond same day.',
  },
  {
    q: 'How fast can you come out?',
    a: 'We offer 24 to 48 hour turnaround for most jobs in Central Indiana. Urgent situations are prioritized.',
  },
  {
    q: 'Can you get everything in one trip?',
    a: 'We come prepared based on your photos so most jobs get done efficiently in one visit. For larger estates or heavy volume cleanouts we will tell you exactly what to expect before we schedule, so there are no surprises and no delays.',
  },
  {
    q: 'How is pricing determined?',
    a: 'Pricing is based on volume, access, labor, and disposal requirements. Our photo-based quotes are accurate and transparent with no surprise fees.',
  },
  {
    q: 'Do you take everything?',
    a: "We remove most household items, furniture, appliances, carpet, debris, and renovation waste. We'll let you know upfront if anything falls outside our scope.",
  },
  {
    q: 'What happens to the items you remove?',
    a: 'Through The Hearth Project initiative, we make every effort to donate usable items to families and nonprofits in need before disposing of anything. We believe in giving items a second life wherever possible.',
  },
  {
    q: 'Do you serve my area?',
    a: "We currently serve Columbus, Indianapolis, Lafayette, Bloomington, Greenwood, Franklin, Seymour, and surrounding Central Indiana communities. Not sure? Contact us and we'll let you know.",
  },
  {
    q: 'Are you insured?',
    a: 'Yes. Ohana Property & Transition Services LLC carries liability insurance for all jobs.',
  },
  {
    q: "Are there items you won't take?",
    a: 'Ohana Property Services does not accept hazardous waste, medical waste, needles or sharps, chemicals, asbestos-containing materials, or regulated waste unless handled by a properly licensed partner. We accept standard nonhazardous cleanout items: furniture, household junk, move-out debris, estate cleanout items, and donation-eligible household goods with written permission. If your items fall outside our scope we will help you find the right disposal resource.',
  },
  {
    q: 'How much does a property cleanout cost?',
    a: 'Standard jobs fall between $100 and $800. Full property and estate jobs are scoped individually from photos.',
  },
  {
    q: 'Why do you need photos to give a quote?',
    a: 'Photos help estimate load size and identify special handling needs so the quote is accurate before arrival.',
  },
  {
    q: 'Do I need to be there when you arrive?',
    a: 'No. Access through a lockbox, unlocked door, or someone on-site is sufficient. We text on arrival and completion.',
  },
  {
    q: 'What happens to the items you remove?',
    a: 'Usable items go to Indiana charities and nonprofits. Everything else goes to Bartholomew County Landfill. CFC appliance recycling fees are passed through at cost.',
  },
  {
    q: "I'm a property manager. Do you offer ongoing service?",
    a: 'Yes. Contact us at (812) 302-6833 to discuss portfolio needs and scheduling.',
  },
];

export const WHO_WE_HELP = [
  {
    title: 'Homeowners',
    body: 'Reclaim your garage, finish a renovation, or clear out years of accumulation.',
    icon: 'House',
  },
  {
    title: 'Landlords',
    body: 'Same-week tenant turnovers so units are back on market faster.',
    icon: 'Building',
  },
  {
    title: 'Real Estate Investors',
    body: 'Flip-ready cleanouts coordinated with your closing and contractor schedules.',
    icon: 'TrendingUp',
  },
  {
    title: 'Real Estate Agents',
    body: 'Estate listings made move-in ready. We work directly with the seller and family.',
    icon: 'Briefcase',
  },
];

export const JOB_TYPES = [
  'Junk Removal',
  'Full Property Cleanout',
  'Estate Cleanout',
  'Appliance Removal',
  'Furniture Removal',
  'Debris Removal',
  'Yard Waste Removal',
  'Donation-Conscious Recovery',
  'Other',
];

export const TIMELINES = [
  'ASAP / Urgent (within 48 hours)',
  'This Week',
  'Within 2 Weeks',
  'Flexible / Just Getting a Quote',
];
